import { getFragmentData, type FragmentType } from '@packages/gql/generated/shopping';
import { ArticleServicesServiceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ArticleServicesServiceFragmentFragmentDoc';
import { Box, Stack, Typography, Image, Checkbox, Dialog, MuiIconButton } from '@packages/shared';
import { Checkmark32, InformationCircle32 } from '@packages/themes/icons';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { ArticleServicesCurrencyFragmentFragmentDoc } from '@packages/gql/generated/shopping/ArticleServicesCurrencyFragmentFragmentDoc';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks/useLayerTracking/useLayerTracking';
import { selectedServices } from '@packages/modules/src/atoms';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { usePriceFormater } from '../../../hooks/usePriceFormater';
import { useArticleServicesTracking } from '../../Tracking/hooks/useArticleServicesTracking';
import { articleServicesMessages } from '../messages';

const iconSize = 30;

/* GraphQL */ `
  fragment ArticleServicesServiceFragment on Service {
    serviceIcon
    uuid
    name
    price
    gratis
    preselected
    mandatory
  }
`;
/* GraphQL */ `
  fragment ArticleServicesCurrencyFragment on ProductPrice {
    currency
  }
`;

export type ArticleServicesClusterProps = {
  data: FragmentType<typeof ArticleServicesServiceFragmentFragmentDoc>;
  /* should be in aPlus price value */
  currencyData: FragmentType<typeof ArticleServicesCurrencyFragmentFragmentDoc>;
};

/**
 * This component is part of the `ArticleServices` component. It displays a single service with:
 * <ul>
 *  <li>Checkbox</li>
 *  <li>Icon</li>
 *  <li>Service Name</li>
 *  <li>Price</li>
 *  <li>"Gratis/Inklusive" Text</li>
 *  <li>More Info Icon</li>
 * </ul>
 * <i>Note: This component should only be used within the `ArticleServices` component.</i>
 */
export const ArticleServicesService = ({ data, currencyData }: ArticleServicesClusterProps) => {
  const { uuid, gratis, mandatory, serviceIcon, name, price, preselected } = getFragmentData(
    ArticleServicesServiceFragmentFragmentDoc,
    data,
  );
  const { currency } = getFragmentData(ArticleServicesCurrencyFragmentFragmentDoc, currencyData);

  const formatPrice = usePriceFormater('APLUS');
  const { formatMessage } = useIntl();

  const { trackingGlycerinDisplayLink, trackingGlycerinLinkClick } = useArticleServicesTracking();
  const { trackViewServiceLayer } = useLayerTracking();

  const [selected, setSelected] = useState(Boolean(preselected));
  const setAtomSelected = useSetAtom(selectedServices);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setAtomSelected((prev) =>
      selected
        ? prev.concat({ sku: uuid, selected: true })
        : prev.filter((service) => service.sku !== uuid),
    );
  }, [selected, setAtomSelected, uuid]);

  // initial tracking
  useEffect(() => {
    trackingGlycerinDisplayLink({
      category: uuid,
      detail: name || undefined,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMoreInfo = () => {
    trackingGlycerinLinkClick({
      category: uuid,
      detail: name || undefined,
    });
    trackViewServiceLayer(name);
    setIsModalOpen(true);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        flexWrap="nowrap"
        alignItems="flex-start"
      >
        {/* Checkmark or Checkbox item */}
        <Box height={`${iconSize}px`} marginRight={2} display="flex" alignItems="center">
          {mandatory ? (
            <Checkmark32 fontSize="medium" color="success" aria-checked />
          ) : (
            <Checkbox
              clickTrackingProps={{
                category: uuid,
                detail: name || undefined,
                label: 'APlus Service',
              }}
              sx={{ padding: 0 }}
              checked={selected}
              onChange={() => setSelected((prev) => !prev)}
              id={uuid}
              aria-checked={selected}
              size="medium"
            />
          )}
        </Box>

        {/* Label with icon, description and price */}
        <Stack
          direction="row"
          gap={1}
          alignItems="flex-start"
          flex={1}
          flexWrap="nowrap"
          justifyContent="space-between"
          component={mandatory ? 'div' : 'label'}
          {...(!mandatory && { htmlFor: uuid })}
        >
          {/* service icon and name wrapper */}
          <Stack direction="row" gap={1} alignItems="flex-start">
            {/* service icon */}
            <Image
              src={`${serviceIcon}.svg`}
              alt=""
              width={iconSize}
              height={iconSize}
              unoptimized
            />
            {/* service mame */}
            {/* this margin is needed to place the name at the baseline of the first row to the center of the other item in this row */}
            <Typography variant="body2" marginTop="0.2em" sx={{ hyphens: 'auto' }}>
              {name}
            </Typography>
          </Stack>
          {!gratis && price && (
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontWeight: 'fontWeightSemiBold',
                lineHeight: `${iconSize}px`,
                whiteSpace: 'nowrap',
              }}
            >
              +{formatPrice(price, currency)}
            </Typography>
          )}
          {gratis && (
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontWeight: 'fontWeightSemiBold',
                color: 'success.main',
                lineHeight: `${iconSize}px`,
                whiteSpace: 'nowrap',
              }}
            >
              {mandatory
                ? formatMessage(articleServicesMessages.articleServicesInclusive)
                : formatMessage(articleServicesMessages.articleServicesFree)}
            </Typography>
          )}
        </Stack>
        {/* more info button */}
        <MuiIconButton
          onClick={handleMoreInfo}
          size="small"
          aria-haspopup
          aria-label={formatMessage(articleServicesMessages.articleServicesMoreInfo)}
        >
          <InformationCircle32 color="info" fontSize="small" />
        </MuiIconButton>
      </Stack>
      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          title={name || ''}
          dialogContent={<ServiceLayer serviceLayerId={`a-plus_${uuid}`} />}
          displayTrackingProps={{
            category: uuid,
            detail: name || undefined,
            label: 'APlus Service',
          }}
        />
      )}
    </>
  );
};
